import { Location } from '@angular/common';
import { computed, effect, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EventType, NavigationBehaviorOptions, Router, RoutesRecognized } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { filter, map, scan, startWith } from 'rxjs/operators';

import { AppData, AppRouteId, getPathForRouteId } from '../../../route-data';
import { StackLocationStrategy } from '../location.strategy';

const emptyMap: ReadonlyMap<string, AppData> = Object.freeze(new Map<string, AppData>());
const pathDataSessionStorageKey = 'cdf-navigation-path-data';

@Injectable({ providedIn: null })
export class NavigationService {
  private readonly locationStrategy = inject(StackLocationStrategy);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly logger = inject(NGXLogger);

  private readonly pathStack = toSignal(
    this.router.events.pipe(
      startWith(this.locationStrategy.getVirtualPathStack()),
      map(() => this.locationStrategy.getVirtualPathStack()),
    ),
    { requireSync: true },
  );

  private readonly pathData: Signal<ReadonlyMap<string, AppData>> = toSignal(
    this.router.events.pipe(
      // only RoutesRecognized events
      filter((event): event is RoutesRecognized => event.type === EventType.RoutesRecognized),
      scan(
        (acc, event) => new Map(acc).set(event.urlAfterRedirects, event.state.root.firstChild?.data as AppData),
        this.loadPathData(),
      ),
      startWith(this.loadPathData()),
    ),
    { requireSync: true },
  );

  private readonly storePathData = effect(() => {
    sessionStorage.setItem(pathDataSessionStorageKey, JSON.stringify(Object.fromEntries(this.pathData().entries())));
  });

  private loadPathData(): ReadonlyMap<string, AppData> {
    const data = sessionStorage.getItem(pathDataSessionStorageKey);
    return data ? new Map(Object.entries(JSON.parse(data) as { [s: string]: AppData })) : emptyMap;
  }

  readonly previousPathData: Signal<AppData | undefined> = computed(() => {
    const path = this.pathStack().at(-2);
    if (path !== undefined) {
      return this.pathData().get(path);
    } else {
      return undefined;
    }
  });

  readonly getCurrentPath: Signal<AppData | undefined> = computed(() => {
    const path = this.pathStack().at(-1);
    if (path) {
      return this.pathData().get(path);
    }
    return undefined;
  });

  back() {
    this.location.back();
  }

  async navigateToRouteId(
    routeId: AppRouteId,
    { params, ...extras }: NavigationBehaviorOptions & { params?: Record<string, string> } = {},
  ) {
    let path = getPathForRouteId(routeId, this.router.config);
    if (path === undefined) {
      this.logger.warn(`#CDFTF0162 Tried routing to "${routeId}" but no route found.`);
      return;
    }
    for (const [key, value] of Object.entries(params ?? {})) {
      path = path.replaceAll(`:${key}`, value);
    }
    await this.router.navigate([path], extras);
  }
}
